import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Skeleton, Typography } from 'antd';
import cn from 'classnames';
import { Trans } from 'react-i18next';

import { RequestStatus, Rules, WithdrawalRequestsStatus } from 'utils/constants';
import Utils from 'utils/utils';

import { getWithdrawals, sendWithdrawalRequest } from 'features/funding/actions';

import { FormItem, SelectField, SubmitButton, TextField } from 'componets/form';
import { EmptyComponent, RejectedMessage, TableItemStatus } from 'componets/ui';
import { Withdrawal2FA } from './Withdrawal2FA';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { ReactComponent as AddIcon } from 'assets/icons/add.svg';

import { FieldData, SendWithdrawalRequestProps } from 'models';
import { selectIsAdmin, selectOverview, selectUser } from 'selectors';

import s from '../index.module.scss';

type WithdrawalProps = {
	onAddFunds: () => void;
};

export const Withdrawal: FC<WithdrawalProps> = ({ onAddFunds }) => {
	const [form] = Form.useForm();

	const [fields, setFields] = useState<FieldData[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [uuid, setUUID] = useState('');

	const t = useTranslate([
		'addFundsText',
		'requestWithdrawText',
		'availableAmountText',
		'withdrawAmountText',
		'accountToFundsText',
		'withdrawFundsText',
		'withdrawRequestsText',
		'selectBankAccountText',
		'confirmText',
		'withdrawFundsNoteText5'
	]);

	const { withdrawals, status: requestStatus } = useAppSelector((state) => state.funding);

	const user = useAppSelector(selectUser);
	const overview = useAppSelector(selectOverview);
	const isAdmin = useAppSelector(selectIsAdmin);

	const dispatch = useAppDispatch();

	const onFinish = (data: SendWithdrawalRequestProps) => {
		setIsSubmitting(true);
		dispatch(sendWithdrawalRequest(data))
			.unwrap()
			.then((body: { data: { withdrawalUuid: string } }) => {
				setIsOpen(true);
				setUUID(body.data.withdrawalUuid);
				dispatch(getWithdrawals());
				form.resetFields();
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields, 'account');
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	useEffect(() => {
		dispatch(getWithdrawals());
	}, [dispatch]);

	const accountOptions = useMemo(
		() =>
			user.accounts.map((item) => ({
				label: item.account,
				value: item.account,
			})),
		[user.accounts],
	);

	useEffect(() => {
		if (isLoading && withdrawals.data?.length === 0) {
			setIsLoading(false);
		}
	}, [isLoading, withdrawals.data?.length]);

	const isPositiveBalance = +overview.availableMoney > 0;

	const isWaitingApprove = useMemo(
		() =>
			withdrawals.data?.find((item) => item.status === WithdrawalRequestsStatus.WAITING_APPROVE),
		[withdrawals.data],
	);

	if (requestStatus === RequestStatus.REJECTED) {
		return <RejectedMessage />;
	}

	return (
		<div className={s.withdrawal}>
			<div className={s.paper}>
				<Typography.Title level={4}>{t.requestWithdrawText}</Typography.Title>
				<div className={s.paperContent}>
					<div className={s.amount}>
						<Typography.Paragraph className={s.subTitle}>
							{t.availableAmountText}:
						</Typography.Paragraph>
						<Typography.Paragraph className={s.money}>
							{Utils.moneyFormat(+overview.availableMoney)}
						</Typography.Paragraph>
						{!isPositiveBalance && (
							<Button onClick={onAddFunds} icon={<AddIcon />} className={s.addFunds}>
								{t.addFundsText}
							</Button>
						)}
					</div>

					<div className={s.form}>
						<div className={s.formWrapper}>
							<Form onFinish={onFinish} onFieldsChange={setFields} fields={fields} form={form}>
								<FormItem
									name="amount"
									className={s.withdrawalInput}
									rules={[
										Rules.REQUIRED,
										Rules.LOW_BALANCE(+overview.availableMoney),
										Rules.NO_LESS_THAN(),
									]}
									normalize={(v) => Utils.transformToFixedNumber(v)}
								>
									<TextField label={t.withdrawAmountText} />
								</FormItem>

								<FormItem
									className={cn(s.withdrawalInput, 'selectField')}
									name="account"
									rules={[Rules.REQUIRED]}
								>
									<SelectField
										options={accountOptions}
										placeholder={t.accountToFundsText}
										selectText={t.selectBankAccountText}
									/>
								</FormItem>

								<SubmitButton
									loading={isSubmitting}
									disabled={!isPositiveBalance}
									text={t.withdrawFundsText}
									className={cn(s.withdrawalSubmit, { 'ant-btn-grey': isAdmin })}
								/>
							</Form>
						</div>

						<div className={`${s.formWrapper} ${s.notes}`}>
							<Typography.Paragraph type="secondary" className={s.text}>
								<Trans i18nKey="pleaseNote" />
								<ul>
									<li><Trans i18nKey="withdrawFundsNoteText" /></li>
									<li><Trans i18nKey="withdrawFundsNoteText2" /></li>
									<li><Trans i18nKey="withdrawFundsNoteText3" /></li>
									<li><Trans i18nKey="withdrawFundsNoteText4" /></li>
								</ul>
								<span dangerouslySetInnerHTML={{ __html: t.withdrawFundsNoteText5 }}/>
							</Typography.Paragraph>
						</div>
					</div>
				</div>
			</div>

			<div className={cn(s.paper, s.scroll)}>
				<Typography.Title level={4}>{t.withdrawRequestsText}</Typography.Title>

				<div className={s.withdrawalItems}>
					<Skeleton loading={RequestStatus.PENDING === requestStatus || isLoading}>
						{withdrawals.data?.map((item, i: number) => {
							if (i === withdrawals.data?.length - 1 && isLoading) {
								setIsLoading(false);
							}

							return (
								<div
									key={item.id}
									className={cn(s.withdrawalItem, { [s.waiting]: isWaitingApprove })}
								>
									<Typography.Text className={s.withdrawalItemText} type="secondary">
										{item.id}
									</Typography.Text>
									<Typography.Text className={s.withdrawalItemText}>{item.date}</Typography.Text>
									<Typography.Text className={s.withdrawalItemText}>
										{Utils.moneyFormat(+item.amount)}
									</Typography.Text>
									<Typography.Text className={s.withdrawalItemText}>
										{item.bank_account?.account}
									</Typography.Text>

									<TableItemStatus
										status={
											item.status === WithdrawalRequestsStatus.CANCELED
												? 'cancelledText'
												: item.status
										}
										isRed={item.status === WithdrawalRequestsStatus.CANCELED}
										isBlue={item.status === WithdrawalRequestsStatus.REFUNDED}
										isGreen={item.status === WithdrawalRequestsStatus.SUCCESSFUL}
									/>
									<Typography.Text className={s.confirm}>
										{item.status === WithdrawalRequestsStatus.WAITING_APPROVE ? (
											<Button
												type="primary"
												onClick={() => {
													setUUID(item.uuid);
													setIsOpen(true);
												}}
											>
												{t.confirmText}
											</Button>
										) : (
											''
										)}
									</Typography.Text>
								</div>
							);
						})}

						{withdrawals.data?.length === 0 && <EmptyComponent />}
					</Skeleton>
				</div>
			</div>

			{isOpen && <Withdrawal2FA isOpen={isOpen} onClose={() => setIsOpen(false)} uuid={uuid} />}
		</div>
	);
};
